import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { getCompensationMethod } from "@redotech/redo-customer-portal-shared/return-review/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  CompensationMethod,
  DraftReturn,
  ReturnTotalCalculationsOutput,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  getShipmentMethod,
  hasReturnItemsThatRequireShipping,
} from "@redotech/redo-model/draft-return/util";
import { getResource } from "@redotech/redo-model/localization/resource";
import { Provider } from "@redotech/redo-model/order";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  Address,
  Payer,
  ProvisionType,
} from "@redotech/redo-model/return-flow";
import { ReturnStatus } from "@redotech/redo-model/return-status";
import { Flex } from "@redotech/redo-web/flex";
import Logo from "@redotech/redo-web/icon-old/logo-icon.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { memo, useEffect, useState } from "react";
import { User } from "../user";
import {
  FeeAndSummary,
  InStoreReturnItems,
  KeepingItems,
  NewItems,
  PickupCard,
  ProvisionSelection,
  ReturnInStoreCard,
  ReturnShippingItems,
  ReviewPageHeader,
  ShippingInfoAndAddressModals,
} from "./review-components";
import { SettlementCard } from "./review-components/shipment-method-cards/settlement-card";
import * as review from "./review.module.css";

export const Review = memo(function Review({
  initialDraftReturn,
  settings,
  user,
  client,
  callback,
}: {
  initialDraftReturn: DraftReturn;
  settings: ReturnAppSettings;
  user?: User;
  client: CustomerPortalRpcClient;
  callback?: string;
}) {
  const size = useScreenType();
  const compensationMethod = getCompensationMethod(initialDraftReturn);

  const [draftReturn, setDraftReturn] =
    useState<DraftReturn>(initialDraftReturn);

  const [creatingStripePayment, setCreatingStripePayment] =
    useState<boolean>(false);

  const [stripe, setStripe] = useState<Stripe | undefined>(undefined);
  const stripeLoad = useLoad(async () => {
    if (stripe || !client) {
      return;
    }
    const { publicKey } = await client.getStripePublicKey({});
    const loadedStripe = await loadStripe(publicKey);
    loadedStripe && setStripe(loadedStripe);
  }, [client]);

  const shipmentLoad = useLoad(async () => {
    if (!client) {
      return;
    }
    await client.getShippingEstimate({ draftReturnId: draftReturn._id });
    const { draftReturn: updatedDraftReturn } = await client.getDraftReturn({
      draftReturnId: draftReturn._id,
    });
    if (updatedDraftReturn) {
      setDraftReturn(updatedDraftReturn);
    }
  }, [client]);

  const calculationsLoad = useLoad(async (): Promise<
    ReturnTotalCalculationsOutput | undefined
  > => {
    if (!client) {
      return;
    }
    const { calculations } = await client.calculateReturnTotals({
      draftReturnId: draftReturn._id,
    });
    return calculations;
    // TODO Having 'draftReturn' as a dependency here will re-load the calculations whenever the draftReturn changes
    //  (ie, when the customer accepts in-store return or settlement). That will re-render the entire page, even though only
    //  the summary card should need to re-render. But if we remove this, the page will not update when the customer changes
    //  their shipping method. Come up with a better solution for this later.
  }, [client, draftReturn.shipmentMethod]);

  const updateProvisionType = useHandler(
    async (provisionType: ProvisionType) => {
      setDraftReturn({ ...draftReturn, provisionType: provisionType });
      const newDraftReturn = (
        await client.setProvisionType({
          draftReturnId: draftReturn._id,
          provisionType: provisionType,
        })
      ).draftReturn;

      setDraftReturn(newDraftReturn);
    },
  );

  const newOrderMethod = draftReturn.provisionType ?? ProvisionType.INSTANT;
  const calculations = calculationsLoad?.value?.[newOrderMethod];

  const [provisionUpdateStatus, triggerProvisionProcessUpdate] = useTriggerLoad(
    async (signal) => {
      await updateProvisionType(ProvisionType.PROCESSED);
    },
  );

  //Accounts for the edge case where instantExchangeOnly is true and provisionType is Instant but the customer does not have new order items
  useEffect(() => {
    if (!calculations) {
      return;
    }
    const hasNewItems =
      calculations?.hasNonGreenReturnItems && calculations?.showNewItems;
    if (
      settings.exchanges.instantExchangeOnly &&
      draftReturn.provisionType === ProvisionType.INSTANT &&
      !hasNewItems &&
      !provisionUpdateStatus.pending
    ) {
      triggerProvisionProcessUpdate();
    }
  }, [
    draftReturn.provisionType,
    settings.exchanges.instantExchangeOnly,
    calculations?.hasNonGreenReturnItems,
    calculations?.showNewItems,
    provisionUpdateStatus,
    triggerProvisionProcessUpdate,
    calculations,
  ]);

  const exchanges = (
    draftReturn.returnType === ReturnTypeEnum.RETURN
      ? settings.automation?.returnProcessing
      : settings.automation?.claimProcessing
  )?.exchanges;

  if (stripeLoad.pending || !stripe || shipmentLoad.pending || !calculations) {
    return <LoadingRedoAnimation />;
  }

  const {
    hasGreenReturnItems,
    hasNonGreenReturnItems,
    showNewItems,
    pickupAccept,
    pickupFee,
    inStoreAccept,
    settlementAccept,
    settlementRefund,
    shippingFee,
    newOrderValue,
    returnValue,
  } = calculations;
  const shipmentMethod = getShipmentMethod(draftReturn);
  const isShippingRequired = hasReturnItemsThatRequireShipping(draftReturn);

  // Show the pickup card if no other shipment methods are selected, there are items that require shipping,
  //   there's no manual review, the pickup fee is non-zero, and the customer chose pickup.
  // FIXME Couldn't this just be whether the selected shipment method is PICKUP?
  //   If the customer has selected PICKUP, we should show the pickup card no matter what, right?
  const showPickup =
    !inStoreAccept &&
    !settlementAccept &&
    isShippingRequired &&
    !compensationMethod.manualReview &&
    pickupFee !== null &&
    pickupFee !== undefined &&
    pickupFee > 0 &&
    shipmentMethod === ShipmentMethodName.PICKUP;
  // Show the in-store return card if no other shipment methods are selected, there are items that require shipping,
  //  there's no manual review, and the merchant has in-store returns enabled.
  // FIXME Instead of checking whether pickup or settlement have been accepted, why not just check if the shipment method is still SELF_SHIP?
  const showInStoreReturn =
    !pickupAccept &&
    !settlementAccept &&
    isShippingRequired &&
    !compensationMethod.manualReview &&
    settings?.returnInStoreEnabled === true;
  // Show the settlement card if no other shipment methods are selected, there are items that require shipping,
  //  there's no manual reivew, the merchant has settlement enabled, the settlement refund is non-zero,
  //  and the value of returnable items is non-zero.
  // FIXME Instead of checking whether pickup or in-store have been accepted, why not just check if the shipment method is still SELF_SHIP?
  const showSettlement =
    !pickupAccept &&
    !inStoreAccept &&
    isShippingRequired &&
    !compensationMethod.manualReview &&
    settings.returns?.settlementEnabled === true &&
    settlementRefund > 0 &&
    compensationMethod.totalReturnableValue.gt(0);

  const copyResource = getResource({
    variant: pickupAccept ? "pickup" : draftReturn.returnType,
    overrides: settings.resourceOverride,
  });

  return (
    <>
      <ReviewPageHeader
        displayHeaderOnly={false}
        draftReturn={draftReturn}
        headerText={copyResource.REVIEW_HEADER_TEXT}
        settings={settings}
      />
      <div className={review.contentWrapper}>
        <div className={review.leftContent}>
          {size === ScreenType.DESKTOP && (
            <Flex // Temporary fix until UI v4.0 - puts a small gap between cards and before next header
              dir="column"
              gap="md"
              mb="md"
            >
              {showPickup &&
                draftReturn?.shipmentMethod?.method ===
                  ShipmentMethodName.PICKUP && (
                  <PickupCard pickup={draftReturn?.shipmentMethod.pickupData} />
                )}
              {showSettlement && (
                <SettlementCard
                  client={client}
                  draftReturn={draftReturn}
                  orderValue={compensationMethod.totalReturnableValue}
                  setDraftReturn={setDraftReturn}
                  settlementRefund={settings?.returns?.settlementRefund ?? 0}
                />
              )}
              {showInStoreReturn && (
                <ReturnInStoreCard
                  client={client}
                  draftReturn={draftReturn}
                  setDraftReturn={setDraftReturn}
                />
              )}
            </Flex>
          )}
          {(compensationMethod.payer === Payer.REDO ||
            compensationMethod.payer === Payer.MERCHANT ||
            shippingFee === 0) &&
            !showPickup &&
            !settings.hidePortalBranding &&
            draftReturn.returnType !== ReturnTypeEnum.CLAIM &&
            draftReturn.returnType !== ReturnTypeEnum.WARRANTY && (
              <div className={review.covered}>
                <Logo className={review.logo} />
                {copyResource.REVIEW_LABEL_COVERAGE}{" "}
                {`${
                  compensationMethod.payer === Payer.REDO
                    ? "Redo"
                    : `${settings?.theme?.merchantDisplayName ?? settings.merchantName}`
                }`}
              </div>
            )}
          {draftReturn?.shipmentMethod?.method ===
          ShipmentMethodName.IN_STORE ? (
            <InStoreReturnItems
              draftReturn={draftReturn}
              newOrderValue={newOrderValue}
              returnValue={returnValue}
              settings={settings}
            />
          ) : (
            hasGreenReturnItems && (
              <KeepingItems
                draftReturn={draftReturn}
                newOrderValue={newOrderValue}
                returnValue={returnValue}
                settings={settings}
              />
            )
          )}
          {hasNonGreenReturnItems && (
            <ReturnShippingItems
              copyResource={copyResource}
              draftReturn={draftReturn}
              newOrderValue={newOrderValue}
              returnValue={returnValue}
              settings={settings}
            />
          )}
          {showNewItems && (
            <NewItems copyResource={copyResource} draftReturn={draftReturn} />
          )}
          <ShippingInfoAndAddressModals
            calculations={calculations}
            client={client}
            copyResource={copyResource}
            draftReturn={draftReturn}
            returnAddress={draftReturn.customer.shippingAddress as Address}
            setDraftReturn={setDraftReturn}
            settings={settings}
            user={user}
          />
          {shouldShowProvisionSelection(
            settings,
            draftReturn,
            exchanges,
            hasNonGreenReturnItems,
            compensationMethod,
          ) && (
            <ProvisionSelection
              draftReturn={draftReturn}
              newOrderMethod={newOrderMethod}
              setNewOrderMethod={updateProvisionType}
              settings={settings}
            />
          )}
          {size === ScreenType.MOBILE && (
            <FeeAndSummary
              calculations={calculations}
              callback={callback}
              client={client}
              copyResource={copyResource}
              creatingStripePayment={creatingStripePayment}
              draftReturn={draftReturn}
              newOrderMethod={newOrderMethod}
              setCreatingStripePayment={setCreatingStripePayment}
              setDraftReturn={setDraftReturn}
              settings={settings}
              showInStoreReturn={showInStoreReturn}
              showPickup={showPickup}
              showSettlement={showSettlement}
              stripe={stripe}
            />
          )}
        </div>
        {size === ScreenType.DESKTOP && (
          <FeeAndSummary
            calculations={calculations}
            callback={callback}
            client={client}
            copyResource={copyResource}
            creatingStripePayment={creatingStripePayment}
            draftReturn={draftReturn}
            newOrderMethod={newOrderMethod}
            setCreatingStripePayment={setCreatingStripePayment}
            setDraftReturn={setDraftReturn}
            settings={settings}
            showInStoreReturn={showInStoreReturn}
            showPickup={showPickup}
            showSettlement={showSettlement}
            stripe={stripe}
          />
        )}
      </div>
    </>
  );
});

const shouldShowProvisionSelection = (
  settings: ReturnAppSettings,
  draftReturn: DraftReturn,
  exchanges: string | undefined,
  hasNonGreenReturnItems: boolean,
  compensationMethod: CompensationMethod,
) => {
  const providerIsShopify =
    draftReturn.orders?.[0]?.platform === Provider.SHOPIFY;
  const instantExchangesAllowed =
    settings.exchanges.allowInstant && exchanges !== ReturnStatus.OPEN;
  const onlyInstantExchangesAllowed = settings.exchanges.instantExchangeOnly;
  const hasNewOrderItems =
    (draftReturn?.newOrderData?.items || [])?.length > 0 ||
    Object.values(compensationMethod.itemValues).some(
      (item) => item.isVariantExchange,
    );

  return (
    instantExchangesAllowed &&
    providerIsShopify &&
    !onlyInstantExchangesAllowed &&
    hasNewOrderItems &&
    hasNonGreenReturnItems &&
    draftReturn.returnType !== ReturnTypeEnum.CLAIM &&
    draftReturn.returnType !== ReturnTypeEnum.WARRANTY
  );
};
